import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { Badge, Button, Drawer, Empty, Segmented, Tag, Tooltip, Typography } from 'antd'
import Loader from 'components/Loader'
import {
  BellOutlined, CalendarOutlined, CheckOutlined, FileTextOutlined, FireFilled, MailOutlined, NotificationOutlined, SyncOutlined, AuditOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { contarCentral, listarCentral, marcarLeituraCentral } from 'services/notifications/central'
import { notificacaoLegivel } from 'utils/textoIntimacao'
import * as S from './styles'

dayjs.extend(relativeTime)
const { Text } = Typography

const ABAS = [
  { value: 'tudo', label: 'Tudo' },
  { value: 'intimacoes', label: 'Intimações', icon: <MailOutlined /> },
  { value: 'prazos', label: 'Prazos', icon: <CalendarOutlined /> },
  { value: 'revisoes', label: 'Revisões', icon: <AuditOutlined /> },
  { value: 'movimentacoes', label: 'Movimentações', icon: <SyncOutlined /> },
  { value: 'sistema', label: 'Sistema', icon: <NotificationOutlined /> },
]
const ICONE = { intimacoes: <MailOutlined />, prazos: <CalendarOutlined />, revisoes: <AuditOutlined />, movimentacoes: <SyncOutlined />, sistema: <NotificationOutlined /> }
const PRIORIDADE = { urgente: { color: 'red', label: 'Urgente' }, alta: { color: 'orange', label: 'Alta' } }

function grupo(data) {
  const d = dayjs(data)
  if (d.isSame(dayjs(), 'day')) return 'Hoje'
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Ontem'
  if (d.isAfter(dayjs().subtract(7, 'day'))) return 'Nesta semana'
  return 'Anteriores'
}

// Sino + painel sobre a tela: o advogado vê o que chegou sem sair do que está fazendo
export default function NotificationCenter({ atualizarQuando }) {
  const navigate = useNavigate()
  const [aberto, setAberto] = useState(false)
  const [aba, setAba] = useState('tudo')
  const [apenasNaoLidas, setApenasNaoLidas] = useState(false)
  const [itens, setItens] = useState([])
  const [temMais, setTemMais] = useState(false)
  const [carregando, setCarregando] = useState(false)
  const [contagem, setContagem] = useState({ total: 0, porAba: {}, urgentes: 0 })

  const totalAnterior = useRef(null)
  const [balancar, setBalancar] = useState(false)
  useEffect(() => {
    const novo = totalAnterior.current !== null && contagem.total > totalAnterior.current
    totalAnterior.current = contagem.total
    if (!novo) return undefined
    setBalancar(true)
    const t = setTimeout(() => setBalancar(false), 900)
    return () => clearTimeout(t)
  }, [contagem.total])

  const atualizarContagem = useCallback(() => {
    contarCentral().then(setContagem).catch(() => {})
  }, [])

  const carregar = useCallback(async (mais = false) => {
    setCarregando(true)
    try {
      const antesDe = mais && itens.length ? itens[itens.length - 1].criadaEm : undefined
      const r = await listarCentral({ aba: aba === 'tudo' ? undefined : aba, naoLidas: apenasNaoLidas ? 1 : undefined, antesDe, limite: 30 })
      setItens((prev) => (mais ? [...prev, ...r.itens] : r.itens))
      setTemMais(r.temMais)
    } catch {
      if (!mais) setItens([])
    } finally {
      setCarregando(false)
    }
  }, [aba, apenasNaoLidas, itens])

  // Contador do sino: ao abrir a tela, a cada minuto e quando chega notificação em tempo real
  useEffect(() => {
    atualizarContagem()
    const id = setInterval(atualizarContagem, 60000)
    return () => clearInterval(id)
  }, [atualizarContagem])
  useEffect(() => { if (atualizarQuando) atualizarContagem() }, [atualizarQuando, atualizarContagem])
  // leitura feita em Publicações também conta no sino
  useEffect(() => {
    window.addEventListener('yrm:sino-atualizar', atualizarContagem)
    return () => window.removeEventListener('yrm:sino-atualizar', atualizarContagem)
  }, [atualizarContagem])

  useEffect(() => {
    if (aberto) carregar(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, aba, apenasNaoLidas])

  const marcar = async (alvo, lida = true) => {
    await marcarLeituraCentral(alvo, lida).catch(() => {})
    const ids = new Set(alvo.ids || [])
    setItens((prev) => prev.map((i) => (alvo.tudo || alvo.aba || ids.has(i.id) ? { ...i, lida } : i)))
    atualizarContagem()
  }

  const abrirItem = (item) => {
    if (!item.lida) marcar({ ids: [item.id] })
    if (item.link?.rota) {
      setAberto(false)
      navigate(item.link.rota)
    }
  }

  const atencao = useMemo(() => itens.filter((i) => !i.lida && (i.prioridade === 'urgente' || i.prioridade === 'alta')).slice(0, 5), [itens])
  const grupos = useMemo(() => {
    const ids = new Set(aba === 'tudo' && !apenasNaoLidas ? atencao.map((i) => i.id) : [])
    const lista = itens.filter((i) => !ids.has(i.id))
    return ['Hoje', 'Ontem', 'Nesta semana', 'Anteriores']
      .map((g) => [g, lista.filter((i) => grupo(i.criadaEm) === g)])
      .filter(([, l]) => l.length)
  }, [itens, atencao, aba, apenasNaoLidas])

  const opcoesAbas = ABAS.map((a) => {
    const n = a.value === 'tudo' ? contagem.total : contagem.porAba?.[a.value]
    return { value: a.value, label: <span>{a.label}{n > 0 && <S.Contador>{n > 99 ? '99+' : n}</S.Contador>}</span> }
  })

  const linha = (item) => (
    <S.Item key={item.id} type='button' $lida={item.lida} $prioridade={item.prioridade} onClick={() => abrirItem(item)}>
      <S.Icone $aba={item.aba}>{item.prioridade === 'urgente' ? <FireFilled /> : ICONE[item.aba]}</S.Icone>
      <div className='corpo'>
        <div className='titulo'>{notificacaoLegivel(item.titulo).titulo}</div>
        {item.descricao && <div className='descricao'>{notificacaoLegivel('', item.descricao).descricao}</div>}
        <div className='meta'>
          {PRIORIDADE[item.prioridade] && <Tag bordered={false} color={PRIORIDADE[item.prioridade].color} style={{ marginInlineEnd: 6 }}>{PRIORIDADE[item.prioridade].label}</Tag>}
          <Tooltip title={dayjs(item.criadaEm).format('DD/MM/YYYY HH:mm')}><span>{dayjs(item.criadaEm).fromNow()}</span></Tooltip>
          {item.link && <span className='abrir'>· abrir {item.link.tipo === 'publicacao' ? 'publicação' : item.link.tipo}</span>}
        </div>
      </div>
      <Tooltip title={item.lida ? 'Marcar como não lida' : 'Marcar como lida'}>
        <S.Ponto
          role='button'
          aria-label={item.lida ? 'Marcar como não lida' : 'Marcar como lida'}
          $lida={item.lida}
          onClick={(e) => { e.stopPropagation(); marcar({ ids: [item.id] }, !item.lida) }}
        />
      </Tooltip>
    </S.Item>
  )

  return (
    <>
      <S.Sino className={balancar ? 'yrm-balanca' : undefined} type='button' title='Notificações' aria-label={`Notificações${contagem.total ? ` (${contagem.total} não lidas)` : ''}`} onClick={() => setAberto(true)}>
        <Badge count={contagem.total} overflowCount={99} size='small' color={contagem.urgentes ? '#dc2626' : '#2563eb'}>
          <BellOutlined style={{ fontSize: 20, color: '#fff' }} />
        </Badge>
      </S.Sino>

      <Drawer
        open={aberto}
        onClose={() => setAberto(false)}
        width={480}
        title='Notificações'
        destroyOnClose={false}
        styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' } }}
        extra={(
          <Button size='small' icon={<CheckOutlined />} disabled={!contagem.total} onClick={() => marcar(aba === 'tudo' ? { tudo: true } : { aba })}>
            Marcar {aba === 'tudo' ? 'todas' : 'aba'} como lidas
          </Button>
        )}
      >
        <S.Topo>
          <Segmented size='small' value={aba} onChange={setAba} options={opcoesAbas} />
          <S.Filtro type='button' $ativo={apenasNaoLidas} onClick={() => setApenasNaoLidas((v) => !v)}>Só não lidas</S.Filtro>
        </S.Topo>

        <S.Lista>
          {carregando && !itens.length && <Loader compacto texto='Carregando avisos' />}
          {!carregando && !itens.length && (
            <Empty style={{ marginTop: 64 }} image={<BellOutlined style={{ fontSize: 40, color: '#cbd5e1' }} />} description={apenasNaoLidas ? 'Nada pendente por aqui' : 'Nenhuma notificação'} />
          )}

          {aba === 'tudo' && !apenasNaoLidas && atencao.length > 0 && (
            <S.Grupo $destaque>
              <div className='rotulo'><FireFilled /> Precisa de atenção</div>
              {atencao.map(linha)}
            </S.Grupo>
          )}
          {grupos.map(([nome, lista]) => (
            <S.Grupo key={nome}>
              <div className='rotulo'>{nome}</div>
              {lista.map(linha)}
            </S.Grupo>
          ))}
          {temMais && (
            <div style={{ padding: 12, textAlign: 'center' }}>
              <Button onClick={() => carregar(true)} loading={carregando}>Carregar mais</Button>
            </div>
          )}
        </S.Lista>

        <S.Rodape>
          <Text type='secondary' style={{ fontSize: 12 }}><FileTextOutlined /> Intimações e movimentações chegam toda madrugada; prazos avisam às 7h.</Text>
          <Button type='link' size='small' onClick={() => { setAberto(false); navigate('/notifications') }}>Ver página completa</Button>
        </S.Rodape>
      </Drawer>
    </>
  )
}
